import "@assets/css/bootstrap-grid.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton, SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  enterpriseTestimonialSchema,
  EnterpriseTestimonialFormTypes,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { EnterpriseTestimonialData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

const EnterpriseTestimonialForm: React.FC = () => {
  const pathname = usePathname();
  const { searchParams, handleAction } = useHandleAction({});
  const queryClient = useQueryClient();
  const { setIsFormPending } = useSelectionStore();

  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(enterpriseTestimonialSchema),
  });

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<EnterpriseTestimonialData>
  >({
    endpoint: endpoint["enterprise-testimonials"],
    id: pageItem || "",
    key: "enterprise-testimonial",
    params: {
      id: pageItem || "",
    },
    enabled: pageItem ? true : false,
  });

  const handleMutate = (data: any) => {
    const payload: any = {
      reviewer_name: data.reviewer_name,
      reviewer_position_course: data.reviewer_position_course,
      review_text: data.review_text,
      embed_link: data.embed_link || "",
      rating_star: data.rating_star ? Number(data.rating_star) : null,
    };

    if (data.profile_icon) {
      payload.profile_icon = data.profile_icon;
    }

    if (pageItem) {
      payload.id = pageItem;
    }

    return {
      data: payload,
      endpoint: endpoint["enterprise-testimonials"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };

  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    setIsFormPending(false);
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });

      handleAction({ type: "page-close" });
      showToast({
        description: `${pathname} ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };

  const handleError = async (error: any) => {
    setIsFormPending(false);
    console.log(error, "error00");
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
    console.log(error, "error00");
  };

  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;

      if (pageMode === "add") {
        return reset({});
      } else if (firstResult) {
        reset(() => ({
          reviewer_name: firstResult?.reviewer_name || "",
          reviewer_position_course: firstResult?.reviewer_position_course || "",
          review_text: firstResult?.review_text || "",
          embed_link: firstResult?.embed_link || "",
          rating_star: firstResult?.rating_star ?? null,
          profile_icon: firstResult?.profile_icon || null,
        }));
      }
    }
  }, [fetchData?.data, fetchPending]);

  const { mutate } = usePostData();

  const onSubmit: SubmitHandler<EnterpriseTestimonialFormTypes> = (data) => {
    setIsFormPending(true);
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "reviewer_name",
        label: "Reviewer Name",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Reviewer Name",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "reviewer_position_course",
        label: "Position / Course",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Position or Course",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "review_text",
        label: "Review Text",
        colSpan: 12,
        type: "editor",
        height: 150,
        placeholder: "Enter Review Text",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "embed_link",
        label: "Embed Link",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Embed Link",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "rating_star",
        label: "Rating Star",
        colSpan: 6,
        type: "number",
        placeholder: "Enter Rating",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "profile_icon",
        label: "Profile Icon",
        colSpan: 6,
        type: "cropper",
        returnFile: true,
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
    ],
    [control, errors, setValue]
  );

  if (fetchPending && searchParams.get("modal-item"))
    return (
      <>
        <SkeletonTwo />
      </>
    );

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        id={pathname + "testimonial"}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default EnterpriseTestimonialForm;
